import { checkPermissions } from "@elit-hair/elit-tools";
import { GridActionsCellItem, GridActionsCellItemProps, GridColDef } from "@mui/x-data-grid";
import { useMemo } from "react";
import {
    TbBriefcase,
    TbCar,
    TbLockAccess,
    TbQrcode,
    TbQrcodeOff,
    TbRecycle,
    TbRestore,
    TbUserBitcoin,
    TbUserX,
} from "react-icons/tb";

import { PERMISSIONS } from "@/constants/permissions";
import useAuth from "@/providers/hooks/useAuth";
import { useUsersTableActions } from "@/stores/use-users-table";
import { User, UserExtras, UserType } from "@/types/user.types";

function PrintActionItem({ handleClick, ...props }: GridActionsCellItemProps & { handleClick: () => void }) {
    return (
        <>
            <GridActionsCellItem {...props} onClick={handleClick} />
        </>
    );
}

export const UserActionsColumn = (): GridColDef<User> => {
    return {
        field: "actions",
        type: "actions",
        width: 50,
        getActions: ({ row }) => {
            const { userData } = useAuth();
            const pagePermissions = useMemo(
                () => ({
                    edit: checkPermissions(userData?.permissionCodes || [], [PERMISSIONS.TR_SVC_HR_MANAGE_USERS]),
                    updateVehicle: checkPermissions(userData?.permissionCodes || [], [
                        PERMISSIONS.TR_SVC_UPDATE_DRIVER_VEHICLE,
                    ]),
                }),
                [userData]
            );
            const {
                updateDeletionInfo,
                updateLoginStatusInfo,
                updateShowVehicleUpdateDialog,
                updateShowPasswordUpdateDialog,
                updateShiftTypeInfo,
                updateShowUpdateUserLifeCycleDialog,
                updateLifecycleInfo,
                updateRestoreInfo,
            } = useUsersTableActions();
            const actions = useMemo(
                () => [
                    {
                        cond: () => pagePermissions.edit && !row.isDeleted && row.userType !== UserType.ADMIN,
                        component: () => (
                            <PrintActionItem
                                label="Kullanıcıyı Sil"
                                handleClick={() =>
                                    updateDeletionInfo({
                                        userType: row.userType,
                                        id: row.id,
                                        name: row.name,
                                    })
                                }
                                showInMenu
                                icon={<TbUserX />}
                                color="primary"
                                key="delete-user"
                            />
                        ),
                    },
                    {
                        cond: () => pagePermissions.edit && row.isDeleted,
                        component: () => (
                            <PrintActionItem
                                label="Silinen Kullanıcıyı Geri Getir"
                                handleClick={() =>
                                    updateRestoreInfo({
                                        userType: row.userType,
                                        id: row.id,
                                        name: row.name,
                                        identityNo: row.identityNumber,
                                    })
                                }
                                showInMenu
                                icon={<TbRestore />}
                                color="primary"
                                key="activate-user"
                            />
                        ),
                    },
                    {
                        cond: () =>
                            pagePermissions.edit && row.userType !== UserType.ADMIN && !row.isDeleted && row.disabled,
                        component: () => (
                            <PrintActionItem
                                label="Kullanıcının Girişini Aktif Et"
                                handleClick={() =>
                                    updateLoginStatusInfo({
                                        id: row.id,
                                        name: row.name,
                                        disabled: false,
                                    })
                                }
                                showInMenu
                                icon={<TbLockAccess color="primary" />}
                                color="primary"
                                key="deactivate-user"
                            />
                        ),
                    },
                    {
                        cond: () => pagePermissions.edit && row.userType !== UserType.ADMIN && !row.disabled,
                        component: () => (
                            <PrintActionItem
                                label="Kullanıcının Girişini Engelle"
                                handleClick={() =>
                                    updateLoginStatusInfo({
                                        id: row.id,
                                        name: row.name,
                                        disabled: true,
                                    })
                                }
                                showInMenu
                                icon={<TbLockAccess color="primary" />}
                                color="primary"
                                key="block-user"
                            />
                        ),
                    },
                    {
                        cond: () => pagePermissions.updateVehicle && !row.isDeleted && row.userType === UserType.DRIVER,
                        component: () => (
                            <PrintActionItem
                                label="Şoförün Arabasını Güncelle"
                                handleClick={() => updateShowVehicleUpdateDialog(row)}
                                showInMenu
                                icon={<TbCar color="primary" />}
                                color="primary"
                                key="update-vehicle"
                            />
                        ),
                    },
                    {
                        // cond: () => row.userType === UserType.DRIVER,
                        cond: () => pagePermissions.edit && true,
                        component: () => (
                            <PrintActionItem
                                label="Kullanıcının Şifresini Güncelle"
                                handleClick={() => updateShowPasswordUpdateDialog(row)}
                                showInMenu
                                icon={<TbUserBitcoin color="primary" />}
                                color="primary"
                                key="update-password"
                            />
                        ),
                    },
                    {
                        cond: () =>
                            pagePermissions.edit &&
                            row.userType === UserType.DRIVER &&
                            !(row.extras & UserExtras.SHIFT_WITHOUT_VEHICLE),
                        component: () => (
                            <PrintActionItem
                                label="Araçsız mesaiye izin ver"
                                handleClick={() =>
                                    updateShiftTypeInfo({
                                        id: row.id,
                                        isDisabled: true,
                                        name: row.name,
                                    })
                                }
                                showInMenu
                                icon={<TbQrcodeOff color="primary" />}
                                color="primary"
                                key={"allow-user-shift"}
                            />
                        ),
                    },
                    {
                        cond: () =>
                            pagePermissions.edit &&
                            row.userType === UserType.DRIVER &&
                            row.extras & UserExtras.SHIFT_WITHOUT_VEHICLE,
                        component: () => (
                            <PrintActionItem
                                label="Araçsız Mesai iznini kaldır"
                                handleClick={() =>
                                    updateShiftTypeInfo({
                                        id: row.id,
                                        isDisabled: false,
                                        name: row.name,
                                    })
                                }
                                showInMenu
                                icon={<TbQrcode color="primary" />}
                                color="primary"
                                key={"block-user-shift"}
                            />
                        ),
                    },
                    {
                        cond: () => pagePermissions.edit,
                        component: () => (
                            <PrintActionItem
                                label="Çalışma Dönemini Güncelle"
                                handleClick={() => updateShowUpdateUserLifeCycleDialog(row)}
                                showInMenu
                                icon={<TbRecycle color="primary" />}
                                color="primary"
                                key={"recycle-user"}
                            />
                        ),
                    },
                    {
                        cond: () => true,
                        component: () => (
                            <PrintActionItem
                                label="Kullanıcı Çalışma Dönemlerini Görüntüle"
                                handleClick={() => updateLifecycleInfo(row)}
                                showInMenu
                                icon={<TbBriefcase color="primary" />}
                                color="primary"
                                key={"recycle-user"}
                            />
                        ),
                    },
                ],
                [
                    row,
                    updateDeletionInfo,
                    updateLoginStatusInfo,
                    updateShowUpdateUserLifeCycleDialog,
                    updateShiftTypeInfo,
                    updateRestoreInfo,
                    updateLifecycleInfo,
                    pagePermissions,
                    updateShowPasswordUpdateDialog,
                    updateShowVehicleUpdateDialog,
                ]
            );

            const activeActions = useMemo(
                () => actions.filter((item) => item.cond()),
                [row, actions, updateDeletionInfo]
            );
            return activeActions.map((item) => item.component());
        },
    };
};
